import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import ErrorBoundary from './ErrorBoundary';
import { initAnalytics, trackWebVital } from './analytics';
import { reportError } from './ErrorReporter';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { staticRoutesMetadata } from './staticRoutes';

initAnalytics();

// Global error handler for uncaught errors
window.onerror = function (message, source, lineno, colno, error) {
  console.error('[Global Error Handler] Uncaught error:', {
    message,
    source,
    lineno,
    colno,
    error
  });

  const errorObj = error || new Error(message);
  reportError(errorObj, 'window.onerror', {
    source,
    lineno,
    colno
  });

  // Return false to allow default error handling
  return false;
};

// Global handler for unhandled promise rejections
window.onunhandledrejection = function (event) {
  console.error('[Global Error Handler] Unhandled promise rejection:', event.reason);

  const error = event.reason instanceof Error
    ? event.reason
    : new Error(String(event.reason));

  reportError(error, 'window.onunhandledrejection', {
    promise: event.promise?.toString()
  });

  // Don't prevent default behavior
};

// Support for react-snap pre-rendering with React 18
const rootElement = document.getElementById('root');

// Base routes derived from the single source of truth (src/staticRoutes.js)
// Press release routes are dynamically added by injectMetadata.js during build
// At runtime, we check if route starts with /press/ for press releases (but not /press itself)
const PRERENDERED_ROUTES = Object.keys(staticRoutesMetadata);

// Check if we're on a pre-rendered route and if content exists
const currentPath = window.location.pathname;
// Check exact match or if it's a press release route (starts with /press/ and is not just /press)
const isPrerenderedRoute = PRERENDERED_ROUTES.includes(currentPath) ||
  (currentPath.startsWith('/press/') && currentPath !== '/press');
const hasPrerenderedContent = rootElement.hasChildNodes();

const appTree = (
  <React.StrictMode>
    <ErrorBoundary name="Root">
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Only attempt hydration if:
// 1. We're on a route that was pre-rendered
// 2. AND there's actually pre-rendered content in the DOM
if (isPrerenderedRoute && hasPrerenderedContent) {
  // Hydrate pre-rendered content
  // Note: Hydration mismatches may occur due to MUI TouchRipple being present in
  // react-snap's Puppeteer-captured HTML but not in React's initial render.
  // These are expected and recoverable - React automatically reconciles the DOM.
  ReactDOM.hydrateRoot(rootElement, appTree, {
    onRecoverableError: (error, errorInfo) => {
      // Suppress known TouchRipple hydration mismatches (expected with react-snap + MUI)
      // But report any other unexpected hydration errors for monitoring
      const errorMessage = error?.message || String(error);
      const isTouchRippleError =
        errorMessage.includes('Minified React error #418') ||
        errorMessage.includes('Minified React error #423') ||
        errorMessage.includes('Hydration') ||
        (errorInfo?.componentStack && errorInfo.componentStack.includes('TouchRipple'));

      console.log('Hydration recoverable error:', error, errorInfo, isTouchRippleError);

      if (isTouchRippleError) {
        // Silently suppress expected TouchRipple mismatches
        // React recovers automatically without any visual impact
        return;
      }

      // Report unexpected hydration errors for investigation
      reportError(
        error instanceof Error ? error : new Error(errorMessage),
        'index.js:hydration-recoverable-error',
        {
          errorMessage,
          errorInfo,
          currentPath,
          isPrerenderedRoute,
          hasPrerenderedContent
        }
      );
    }
  });
} else {
  if (isPrerenderedRoute && !hasPrerenderedContent) {
    const errorMessage = `Pre-rendered route "${currentPath}" has no pre-rendered content (nginx/build issue?)`;

    reportError(
      new Error(errorMessage),
      'index.js:prerender-invariant',
      {
        currentPath,
        isPrerenderedRoute,
        hasPrerenderedContent,
        expectedRoutes: PRERENDERED_ROUTES
      }
    );
  }

  const root = ReactDOM.createRoot(rootElement);
  root.render(appTree);
}

reportWebVitals(trackWebVital);
